import React, { useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSpring, useChain, animated } from 'react-spring';
import { useTheme } from 'emotion-theming';
import Color from 'color';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Text from '../../../shared/_TBComponents/Text';
import IconRound from '../../../shared/components/IconRound/IconRound';
import { getChangeArrow } from '../helpers';

const BASE_FONT_SIZE = 16; // 1em reference for the scale cascade below

const RIBBON_COLORS = {
  1: { background: '#f9b242', border: '#c98600' },
  2: { background: '#dadada', border: '#979797' },
  3: { background: '#d0740d', border: '#9c4e00' },
};

const PodiumContainer = styled('div')`
  display: flex;
  height: auto;
  width: ${(props) => (props.small ? '100%' : '16.625em')};
  flex-direction: ${(props) => (props.small ? 'row' : 'column')};
  border-radius: ${(props) => (props.small || props.minimized ? '0px' : '0.3125em')};
  background: ${(props) => (`linear-gradient(134deg, ${props.colorAccent} -8%, ${props.darkerColorAccent} 200%)`)};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  padding: ${(props) => (props.small ? '1em 1.875em' : '0px')};
  position: relative;
`;

const Ribbon = styled('div')`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const PodiumAvatar = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding-top: ${(props) => (props.small ? '0px' : '2.125em')};
`;

const PodiumCopy = styled('div')`
  display: flex;
  width: 100%;
  margin-top: ${(props) => (props.small ? '0px' : '0.625em')};
  padding: ${(props) => (props.small ? '0px' : '0 1.25em 1em 1.25em')};
  align-items: center;
`;

const NameContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: ${(props) => (props.minimized ? '0.625em' : '1.25em')};
  min-width: 0;
  flex: 1 1 auto;
`;

// Result (2em) + Change (1em), fixed even when Change is hidden so podiums line
// up across boards that differ in showing it.
const RESULT_WRAPPER_HEIGHT = '3em';

const ResultWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  height: ${RESULT_WRAPPER_HEIGHT};
  ${(props) => (props.small ? 'gap: 0.125em;' : '')}
  margin-left: auto;
  padding-right: ${(props) => ((props.small && !props.minimized) ? '1.875em' : '0px')};
`;

const RibbonBadgeWrapper = styled('span')`
  transform: ${(props) => `scale(${props.scaleFactor})`};
`;

const PodiumCard = ({
  athlete,
  changeDisplayValue,
  className,
  compareRawValue,
  avatar,
  date,
  minimized,
  place,
  rawValue,
  result,
  scaleFactor,
  showChange,
  small,
}) => {
  const theme = useTheme();
  const ribbonRef = useRef();
  const animatedRibbon = useSpring({ from: { transform: 'scale(0)' }, to: { transform: 'scale(1)' }, ref: ribbonRef });

  const avatarRef = useRef();
  const fadeInAvatar = useSpring({ from: { opacity: 0, translate: 'translate3d(0, 100px, 0)' }, to: { opacity: 1, translate: 'translate3d(0px, 0px, 0px)' }, ref: avatarRef });

  const textRef = useRef();
  const fadeInText = useSpring({ from: { opacity: 0 }, to: { opacity: 1 }, ref: textRef });

  const themeColor = theme.colorAccent ? theme.colorAccent : '#FFFFFF';
  const colorAccent = Color(themeColor);
  const darkerColorAccent = colorAccent.darken(0.5);
  const fontSize = `${BASE_FONT_SIZE * scaleFactor}px`;
  const ribbonColors = RIBBON_COLORS[place];

  const resultNumber = parseFloat(result);
  const isNumericResult = !Number.isNaN(Number(result));
  // Shrinks a long value before truncation (below) has to kick in as a last resort.
  const shrinkForLength = (value, baseSize, shrunkSize) => (
    (!small && String(value).length > 6) ? shrunkSize : baseSize
  );
  const resultFontSize = shrinkForLength(result, '1.667em', '1em');

  // Uncolored: green/red doesn't read on the card's dark green background.
  const changeArrow = getChangeArrow(rawValue, compareRawValue);
  const hasChangeValue = changeDisplayValue !== null && changeDisplayValue !== '' && changeDisplayValue !== 0;
  const changeValue = hasChangeValue ? changeDisplayValue : '-';
  const changeText = `Change: ${changeArrow}${changeValue}`;
  const changeFontSize = shrinkForLength(changeValue, '0.667em', '0.5em');

  const numberRef = useRef();
  const animatedNumbers = useSpring(
    { from: { val: 0 }, to: { val: resultNumber }, ref: numberRef },
  );

  useChain([ribbonRef, avatarRef, textRef, numberRef], [0, 0.5, 0.7, 0.8]);

  return (
    <PodiumContainer
      className={className}
      colorAccent={colorAccent}
      darkerColorAccent={darkerColorAccent}
      small={small}
      style={{ fontSize }}
    >
      <Ribbon style={{ transform: `translate(-50%, -50%) scale(${scaleFactor})` }}>
        <animated.div style={animatedRibbon}>
          {(ribbonColors && !small) && (
            <IconRound
              icon='ribbon'
              customBackground={ribbonColors.background}
              customBorder={ribbonColors.border}
              customColor='white'
              xLarge
            />
          )}
        </animated.div>
      </Ribbon>
      {!small && (
        <PodiumAvatar
          small={small}
        >
          <animated.div
            style={{
              ...fadeInAvatar,
            }}
          >
            <Avatar
              avatar={avatar}
              name={athlete}
              size={Math.round((place === 1 ? 64 : 52) * scaleFactor)}
            />
          </animated.div>
        </PodiumAvatar>
      )}
      <animated.div
        style={{
          display: 'flex',
          width: '100%',
          ...fadeInText,
        }}
      >
        <PodiumCopy
          minimized={minimized}
          small={small}
        >
          <Text styledTextProps={{ color: 'white', fontWeight: '900', fontSize: '1.667em' }}>
            {place}
          </Text>
          {(small || minimized) && (
            <animated.div
              style={{
                marginLeft: minimized ? '0.9375em' : '1.25em',
                ...fadeInAvatar,
              }}
            >
              <Avatar
                avatar={avatar}
                name={athlete}
                size={Math.round((minimized ? 40 : 55) * scaleFactor)}
              />
            </animated.div>
          )}
          <NameContainer
            minimized={minimized}
          >
            <Text
              styledTextProps={{ color: 'white', fontWeight: '600', fontSize: '0.833em' }}
              styledWrapperProps={{ minWidth: 0 }}
              width='100%'
              truncate
              tooltip={athlete}
            >
              {athlete}
            </Text>
            <Text styledTextProps={{ color: 'white', fontWeight: '600', fontSize: '0.667em' }} styledWrapperProps={{ opacity: 0.5 }}>
              {date}
            </Text>
          </NameContainer>
          <ResultWrapper small={small} minimized={minimized}>
            {/* Height goes on the Wrapper, not line-height: otherwise it varies with
                resultFontSize and the cards in a row end up different heights. */}
            <Text
              styledTextProps={{ color: 'white', fontSize: resultFontSize, textAlign: 'right' }}
              styledWrapperProps={{ justifyContent: 'center', minWidth: 0 }}
              height='2em'
              width='100%'
              truncate
            >
              {!isNumericResult ? (
                result
              ) : (
                // span, not div: this renders inside Text's <p> (AtomicText), and a
                // block element there breaks the HTML parser out of the <p> early,
                // silently detaching it from the <p>'s truncate/ellipsis styles.
                <animated.span>
                  {resultNumber % 1 !== 0 ? (
                    animatedNumbers.val.interpolate((val) => {
                      const decimals = resultNumber.toString().split('.')[1];
                      const decimalPlaces = decimals.length;

                      return (
                        val.toFixed(decimalPlaces)
                      );
                    })
                  ) : (
                    animatedNumbers.val.interpolate((val) => Math.floor(val))
                  )}
                </animated.span>
              )}
            </Text>
            {showChange && (
              <Text
                styledTextProps={{
                  color: 'white', fontWeight: '600', fontSize: changeFontSize, textAlign: 'right',
                }}
                styledWrapperProps={{ minWidth: 0 }}
                height='1em'
                width='100%'
                truncate
                tooltip={changeText}
              >
                {changeText}
              </Text>
            )}
          </ResultWrapper>
          {(small && !minimized && ribbonColors) && (
            <RibbonBadgeWrapper scaleFactor={scaleFactor}>
              <IconRound
                icon='ribbon'
                customBackground={ribbonColors.background}
                customBorder={ribbonColors.border}
                customColor='white'
                xLarge
              />
            </RibbonBadgeWrapper>
          )}
        </PodiumCopy>
      </animated.div>
    </PodiumContainer>
  );
};

PodiumCard.propTypes = {
  athlete: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  changeDisplayValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  compareRawValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  date: PropTypes.string.isRequired,
  minimized: PropTypes.bool,
  small: PropTypes.bool,
  place: PropTypes.number.isRequired,
  rawValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  result: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  scaleFactor: PropTypes.number,
  showChange: PropTypes.bool,
};

PodiumCard.defaultProps = {
  changeDisplayValue: null,
  className: '',
  compareRawValue: null,
  minimized: false,
  rawValue: null,
  scaleFactor: 1,
  showChange: false,
  small: false,
};

export default PodiumCard;
